import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Translate, translateDateFormat } from '../../../../shared/LanguageService';
import { CustomerBooking } from '../../types';
import RouterLink from '../../shared/view/RouterLink';
import { ScreenPoint, useExpandFromPoint } from '../../shared/state/expandFromPoint';

type BookingDialogProps = {
    booking: CustomerBooking | null;
    open: boolean;
    from: ScreenPoint | null;
    showBrokerFirm: boolean;
    returnTo: string;
    onCancelBooking: () => void;
    onClose: () => void;
};

type DetailProps = {
    label: string;
    value: string | null;
};

function Detail({ label, value }: DetailProps) {
    if (!value) {
        return null;
    }

    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" component="div">
                {label}
            </Typography>
            <Typography variant="body1">{value}</Typography>
        </Box>
    );
}

const TitleId = 'booking-dialog-title';

function orderNumber(booking: CustomerBooking): string {
    return `${booking.orderCollectionNumber}:${booking.orderNumber}`;
}

function kindText(booking: CustomerBooking): string {
    return Translate(
        booking.kind === 'styling'
            ? 'customer-calendar/booking/kind/styling'
            : 'customer-calendar/booking/kind/photo'
    );
}

function bookedTime(booking: CustomerBooking): string {
    let start = new Date(booking.start);
    let end = new Date(booking.end);
    return `${translateDateFormat(start, 'PPPP')}, ${translateDateFormat(
        start,
        'HH:mm'
    )} – ${translateDateFormat(end, 'HH:mm')}`;
}

function rebookAddress(booking: CustomerBooking, returnTo: string): string {
    return `${booking.rebookPath}&redirectTo=${encodeURIComponent(returnTo)}`;
}

function RebookButton({ booking, returnTo }: { booking: CustomerBooking; returnTo: string }) {
    const label = Translate('view-order/basic-order-info/button/rebook');

    if (booking.isRebookLocked) {
        return (
            <Tooltip title={Translate('view-order/basic-order-info/button/rebook/disabled-title')}>
                <span>
                    <Button variant="outlined" disabled>
                        {label}
                    </Button>
                </span>
            </Tooltip>
        );
    }

    return (
        <Button variant="outlined" component={RouterLink} to={rebookAddress(booking, returnTo)}>
            {label}
        </Button>
    );
}

function BookingDialog({
    booking,
    open,
    from,
    showBrokerFirm,
    returnTo,
    onCancelBooking,
    onClose
}: BookingDialogProps) {
    const expandFrom = useExpandFromPoint(from);

    return (
        <Dialog
            {...expandFrom}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            aria-labelledby={TitleId}
        >
            {booking && (
                <>
                    <DialogTitle id={TitleId} sx={{ pb: 0 }}>
                        {booking.address || orderNumber(booking)}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {[
                                [booking.postalNumber, booking.city].filter(Boolean).join(' '),
                                kindText(booking)
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                        <Detail
                            label={Translate('customer-calendar/booking/time')}
                            value={bookedTime(booking)}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4 }}>
                            <Detail
                                label={Translate('customer-calendar/booking/order-number')}
                                value={orderNumber(booking)}
                            />
                            {booking.planOrderId !== null && (
                                <Box sx={{ mb: 1.5 }}>
                                    <Detail
                                        label={Translate('customer-calendar/booking/plan-order')}
                                        value={booking.planOrderNumber}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        component={RouterLink}
                                        to={`/orders/${booking.planOrderId}/plan`}
                                        sx={{ mt: -0.5 }}
                                    >
                                        {Translate('customer-calendar/booking/go-to-plan-order')}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        <Detail
                            label={Translate('customer-calendar/booking/product')}
                            value={booking.product}
                        />
                        <Detail
                            label={Translate(
                                booking.kind === 'styling'
                                    ? 'customer-calendar/booking/stylist'
                                    : 'customer-calendar/booking/photographer'
                            )}
                            value={booking.performedBy}
                        />
                        {showBrokerFirm && (
                            <>
                                <Detail
                                    label={Translate('customer-calendar/booking/broker-agent')}
                                    value={booking.brokerAgentName}
                                />
                                <Detail
                                    label={Translate('customer-calendar/booking/broker-firm')}
                                    value={booking.brokerFirmName}
                                />
                            </>
                        )}
                        <Detail
                            label={Translate('customer-calendar/booking/created-by')}
                            value={booking.createdBy}
                        />
                    </DialogContent>
                    {(booking.canCancel || (booking.canRebook && booking.rebookPath)) && (
                        <DialogActions sx={{ justifyContent: 'flex-start', px: 2, pb: 0 }}>
                            {booking.canCancel && (
                                <Button color="error" onClick={onCancelBooking}>
                                    {Translate('view-order/basic-order-info/button/unbook')}
                                </Button>
                            )}
                            {booking.canRebook && booking.rebookPath && (
                                <RebookButton booking={booking} returnTo={returnTo} />
                            )}
                        </DialogActions>
                    )}
                    <DialogActions>
                        <Button onClick={onClose}>
                            {Translate('customer-calendar/booking/close')}
                        </Button>
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to={`/orders/${booking.orderId}/basic`}
                        >
                            {Translate('customer-calendar/booking/go-to-order')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

export default BookingDialog;
